// src/components/Game.js
import React, { useState, useEffect, useRef, useContext } from 'react';
import axiosInstance from '../axiosConfig';
import { AuthContext } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import '../Chess.css';

const cols = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
const rows = ['1', '2', '3', '4', '5', '6', '7', '8'];

// Utility functions for getting/clearing cookies
function getCookie(name) {
    let cookieValue = '';
    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            // Does this cookie string begin with the name we want?
            if (cookie.substring(0, name.length + 1) === (name + '=')) {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookieValue;
}

function clearCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

const Game = () => {
    const { user } = useContext(AuthContext);
    const [isLoading, setIsLoading] = useState(true); // Loading state
    const [error, setError] = useState('');

    const [srcPos, setSrc] = useState('');
    const [piece, setPiece] = useState('');
    const [destPos, setDest] = useState('');

    const [username, setUsername] = useState('');
    const [opponent, setOpponent] = useState('');

    const [turn, setTurn] = useState('');
    const [srcChanged, setSrcChanged] = useState(false)
    const [destChanged, setDestChanged] = useState(false)
    const [turnError, setTurnError] = useState(false);
    const [srcError, setSrcError] = useState('')
    const [destError, setDestError] = useState('')

    const userId = user?.user_id;
    const [role, setRole] = useState('');
    const navigate = useNavigate();
    const matchid = window.location.pathname.split("/")[2];
    //GameState
    const [chessboard, setChessboard] = useState({});
    const [updated, setUpdated] = useState(false);
    const [resigned, setResigned] = useState(false);


    //SOCKET
    const [isGSConnected, setIsGSConnected] = useState(false);
    const gameSocket = useRef(null);
   
    const isFormValid = !srcError && !destError && srcPos && destPos;

    //FORM HANDLING 
    // Validation functions
    const validateSrc = (value) => {
        if (!value) {
            return 'Piece is required.';
        }
        if (value.length !== 2) {
            return "Piece must be 2 characters long (e.g., 'e2').";
        }
        else if(cols.includes(value[0]) && rows.includes(value[1])){
            if(piece === " " || piece === "&nbsp;"){
                return "Piece is empty and invalid.";
            }
            else{
                return null;
            }
        }
        else if (cols.includes(value[0]) === false ||
          rows.includes(value[1]) === false){
          return "Piece is invalid"
        }
        else {
          return "Use column and row format, [a-h][1-8], e.g. 'e2'.";
        }
        return null; // No errors
    };
    const validateDest = (value) => {
        if (!value) {
            return 'Position is required.';
        }
        if (value.length !== 2) {
            return "Piece must be 2 characters long (e.g., 'e2').";
        }
        else if(cols.includes(value[0]) && rows.includes(value[1])){
            return null;
        }
        else if (cols.includes(value[0]) === false ||
            rows.includes(value[1]) === false){
            return "Piece is invalid"
        }
        else {
            return "Use column and row format, [a-h][1-8], e.g. 'e2'.";
        }
    };
    const updateSrc = (e) => {
        const piece_pos = e.target.value;
        setSrc(piece_pos);
        setPiece(chessboard[piece_pos]["piece"])
        const srcError = validateSrc(piece_pos);
        setSrcError(srcError);
    }
    const updateDest = (e) => {
        const position = e.target.value;
        setDest(position);
        const destError = validateDest(position);
        setDestError(destError);
    }
    const handleSubmit = (e) => {
        e.preventDefault();
        setError(''); // Clear previous errors

        // Set fields as touched to trigger validation display
        setSrcChanged(true);
        setDestChanged(true);
        // Client-side validation
        const srcError = validateSrc(srcPos);
        const destError = validateDest(destPos);

        setSrcError(srcError);
        setDestError(destError);

        if (!srcError && !destError) {
        // If validation passes, proceed to update
        handleUpdate();
        setUpdated(true);
        }
    };

    // Handle cell update
    const handleUpdate = async () => {
        try {
            const response = await axiosInstance.put(`/api/v1/move-piece/`,
            { src: srcPos, dest: destPos, piece: piece, user_id: userId });
            console.log('Move successful');
            setChessboard(Object.fromEntries(
                response.data["board"].map(item => [item.position, item])
            ));
            setTurn(response.data["turn"])
            setSrc('');
            setDest('');
            setSrcError('');
            setDestError('');
            setTurnError(false);
            setError(''); // Clear any previous errors
            setSrcChanged(false);
            setDestChanged(false);
            // Re-fetch the board after update
            //fetchBoard();

        } 
        catch (error) {
            if (error.response) {
                if (error.response.status === 400) {
                    // Validation error from the server
                    const serverErrors = error.response.data;
                    setError('Invalid input.');
                } 
                else if (error.response.status === 401 || error.response.status === 403) {
                    navigate('/login');
                } 
                else {
                    setError('Error updating board cell.');
                    console.error('Error updating board cell:', error);
                }
            } 
            else {
                setError('Network error. Please try again later.');
                console.error('Error updating board cell:', error);
            }
        }
    };



    //BOARD RENDERING
    // Fetch the board data
    const fetchBoard = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/api/v1/get-game/');
            //setChessboard(response.data);
            setChessboard(Object.fromEntries(
                response.data["board"].map(item => [item.position, item])
            ));
            setRole(response.data["role"])
            setTurn(response.data["turn"])
            setUsername(response.data["username"])
            setOpponent(response.data["opponent"])
            setError('');
        } catch (error) {
            if (error.response) {
                if (error.response.status === 401 || error.response.status === 403) {
                    navigate('/login');
                }
                else {
                    setError('Error fetching the board.');
                    console.error('Error fetching the board:', error);
                }
            }       
            else {
                setError('Network error. Please try again later.');
                console.error('Error fetching the board:', error);
            }
        } finally {
            console.log("Successful load")
            setIsLoading(false);
        }
    };

    useEffect(() => {
      // If you need to fetch a real username from the backend, do it here:
      // setUsername(...);
        fetchBoard();
    }, []);

    useEffect(() => {
        const wsScheme = window.location.protocol === "https:" ? "wss" : "ws";
        gameSocket.current = new WebSocket(
            wsScheme + '://' + window.location.host + '/ws/pollGame/' + matchid + '/' + userId + '/'
        );
        // New WebSocket Connection
        gameSocket.current.onopen = function(e) {
            console.log("GameSocket connection established.");
            gameSocket.current.send(JSON.stringify({
                'action': "reconnect",
                'match_id': matchid,
                'updated': updated
            }));
            setIsGSConnected(true)
        // Enable the send button or perform any actions that require an open connection
        }
        //Handles responses from WebSocket
        gameSocket.current.onmessage = (e) => {
            const data = JSON.parse(e.data);
            //console.log(data)
            let action = data["action"];
            if(action === "continue"){
                if(data["updated"]){
                   console.log("updated, refreshing");
                   fetchBoard();
                   setUpdated(false);
                }
            }
            else if(action === "resign"){
                resignUser(data["initiator"])
            }
        }
        gameSocket.current.onerror = (e) => {
            console.error("GameSocket error observed:", e);
        };
    
        gameSocket.current.onclose = (e) => {
            console.log("gameSocket connection closed.");
        };
        return () => {
            console.log('[GameSocket] Cleanup: Closing socket...');
            if (gameSocket.current) {
            gameSocket.current.close();
            }
        };
    });
    //resign user, only user resigning calls request
    const resignUser = async (loser) => {
        if(loser !== username){
            navigate('/chess')
            return
        }
        try {
            const response = await axiosInstance.post(`/api/v1/resign/`);
            console.log('Resign successful');
            navigate('/chess');
        } 
        catch (error) {
            if (error.response) {
                if (error.response.status === 400) {
                    // Validation error from the server
                    const serverErrors = error.response.data;
                    setError('Resign Failure.');
                } 
                else if (error.response.status === 401 || error.response.status === 403) {
                    navigate('/login');
                } 
                else {
                    setError('Error resigning.');
                    console.error('Error resigning:', error);
                }
            } 
            else {
                setError('Network error. Please try again later.');
                console.error('Error updating board cell:', error);
            }
        }
    }
    const handleResign = () => {
        if (gameSocket.current && isGSConnected) {
            gameSocket.current.send(
                JSON.stringify({
                    'action': 'resign'
                })
            );
        } 
        else {
            console.warn('GameSocket is not open.');
            alert('GameSocket connection is not open. Please wait a moment and try again.');
        }
    };
    const renderBoard = () => {
        const colsWhite = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
        const colsBlack = ['h', 'g', 'f', 'e', 'd', 'c', 'b', 'a'];
        const rowsBlack = ['1', '2', '3', '4', '5', '6', '7', '8'];
        const rowsWhite = ['8', '7', '6', '5', '4', '3', '2', '1'];
        return (
            <div className="text-center mt-5">
                <h2>
                    {role === turn ? (
                        `${username}'s turn`
                    ) : (
                        `${opponent}'s turn`
                    )
                    }
                </h2>
                <table id="chessBoard" border="1" class="table-responsive">
                {role === "C" && <>
                    {rowsWhite.map((digit, i) => (
                    <tr>
                        <th>
                        { digit }
                        </th>
                        {colsWhite.map((letter, j) => (
                        <td class="chess_tile" id= {letter + digit} 
                            dangerouslySetInnerHTML={{__html: chessboard[letter+digit]["piece"]}}>
                        </td>
                        ))} 
                    </tr>
                    ))}  
                    <tr id="chessRow">
                    <th></th>
                    <th>A</th>
                    <th>B</th>
                    <th>C</th>
                    <th>D</th>
                    <th>E</th>
                    <th>F</th>
                    <th>G</th>
                    <th>H</th>
                    </tr>
                </>
                }
                {role === "A" && <>
                    {rowsBlack.map((digit, i) => (
                    <tr>
                        <th>
                        { digit }
                        </th>
                        {colsBlack.map((letter, j) => (
                        <td class="chess_tile" id= {letter + digit} 
                            dangerouslySetInnerHTML={{__html: chessboard[letter+digit]["piece"]}}>
                        </td>
                        ))} 
                    </tr>
                    ))}  
                    <tr id="chessRow">
                    <th></th>
                    <th>H</th>
                    <th>G</th>
                    <th>F</th>
                    <th>E</th>
                    <th>D</th>
                    <th>C</th>
                    <th>B</th>
                    <th>A</th>
                    </tr>
                    </>}
                </table>
            </div>
        )
    }

    const renderForm = () => {
        return (
            <div>
              <form id="chessform"onSubmit={handleSubmit} method="POST">
                <label htmlFor="piece">Piece:</label>
                  <input type="text"
                    id="piece"
                    name="piece"
                    placeholder="e2"
                    required
                    autoComplete="off"
                    onChange={updateSrc}
                  />
                <label htmlFor="position">Position:</label>
                  <input type="text"
                    id="position"
                    name="position"
                    placeholder="e4"
                    required
                    autoComplete="off"
                    onChange={updateDest}
                  />
                <input id="move" type="submit" className="btn btn-primary" name="action" disabled={!isFormValid}/>
              </form>
              <input id="resign" type="button" onClick={handleResign} className="btn btn-primary" name="action" value="resign" novalidate/>
              
            </div>
        )
    }

    const loadGame = () => {
        return (
          <div>
            {isLoading ? (
              <div className="text-center mt-5">
                <div className="spinner-border" role="status" style={{ width: '3rem', height: '3rem' }}>
                  <span className="sr-only">Loading...</span>
                </div>
                <p>Loading...</p>
              </div>
            ) : (
              <div>
                <div>{renderBoard()}</div>
                <div>{renderForm()}</div>
              </div>
            )}
          </div>
        )
    }
    // -------------------------------------------
    // RENDER
    // -------------------------------------------
    return (
        <div class="container">
          <h1>Chess</h1>
          {loadGame()}
        </div>
    );
};

export default Game;